import React, { useState, useEffect } from 'react';
import { dsatAPI } from '../lib/api';
import { GlassCard, KpiTile as BrandKpiTile, LogoLockup, EnforcedText } from '../components/shared';
import LoadingSpinner from '../components/LoadingSpinner';
import StatusBadge from '../components/StatusBadge';
import DsatHeatmap from '../components/DsatHeatmap';
import { Target, CheckCircle, Download, MapPin, Zap } from 'lucide-react';
import SEOHead from '../components/SEOHead';

const DsatTargets = () => {
  const [loading, setLoading] = useState(true);
  const [kpis, setKpis] = useState(null);
  const [scores, setScores] = useState([]);
  const [targets, setTargets] = useState([]);
  const [selectedCell, setSelectedCell] = useState(null);
  const [cellBreakdown, setCellBreakdown] = useState(null);
  const [selectedCity, setSelectedCity] = useState('');

  useEffect(() => {
    fetchData();
  }, [selectedCity]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [kpisRes, scoresRes, targetsRes] = await Promise.all([
        dsatAPI.getKPIs(selectedCity),
        dsatAPI.getScores({ city: selectedCity, min_demand_index: 0.3 }),
        dsatAPI.getTargets({ city: selectedCity }),
      ]);

      setKpis(kpisRes.data);
      setScores(scoresRes.data.scores || []);
      setTargets(targetsRes.data.targets || []);
    } catch (error) {
      console.error('Error fetching DSAT data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCellClick = async (cell) => {
    setSelectedCell(cell);
    try {
      const res = await dsatAPI.getCellBreakdown(cell.cell);
      setCellBreakdown(res.data);
    } catch (error) {
      console.error('Error fetching cell breakdown:', error);
    }
  };

  const handleApproveTop10 = async () => {
    const top10 = targets.slice(0, 10).map(t => t.id);
    try {
      await dsatAPI.approveBatch(top10);
      alert('Top 10 targets approved!');
      fetchData();
    } catch (error) {
      console.error('Error approving targets:', error);
    }
  };

  if (loading && !kpis) {
    return <LoadingSpinner size="lg" text="Loading DSAT targets..." />;
  }

  return (
    <>
      <SEOHead 
        title="DSAT Targets - Demand-Based Site Intelligence"
        description="AI-powered site selection for EV charging stations. DemandIndex algorithm for optimal location planning with 80% utilization vs industry 35%."
        path="/planning/dsat"
        type="WebApplication"
        noIndex={true}
      />
      <div data-testid="dsat-targets-page">
        {/* Header with Logo */}
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <LogoLockup size="md" />
          <div>
            <h1 className="text-3xl font-bold text-gray-900">DSAT Targets (Auto-Targeting)</h1>
            <p className="text-sm text-gray-500 mt-1">
              <EnforcedText>Demand-based site allocation and targeting engine</EnforcedText>
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={selectedCity}
            onChange={(e) => setSelectedCity(e.target.value)}
            className="px-4 py-2 border border-gray-200 rounded-2xl text-sm bg-white shadow-sm hover:border-gray-300 transition-colors"
          >
            <option value="">All Cities</option>
            <option value="Delhi">Delhi</option>
            <option value="Mumbai">Mumbai</option>
            <option value="Bangalore">Bangalore</option>
            <option value="Hyderabad">Hyderabad</option>
            <option value="Chennai">Chennai</option>
            <option value="Pune">Pune</option>
          </select>
          <button className="px-4 py-2 text-sm font-medium text-white bg-brand hover:opacity-90 rounded-2xl transition-opacity flex items-center gap-2 shadow-sm">
            <Download size={16} />
            Export Target Plan
          </button>
        </div>
      </div>

      {/* KPI Row */}
      {kpis && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4 mb-6">
          <BrandKpiTile
            title="AC Build Target"
            value={kpis.ac_build_target}
            accentBar="link"
            footer="Definition: AC units target • Source: PMO • Updated: 1 day ago"
          />
          <BrandKpiTile
            title="DC Build Target"
            value={kpis.dc_build_target}
            accentBar="primary"
            footer="Definition: DC units target • Source: PMO • Updated: 1 day ago"
          />
          <BrandKpiTile
            title="Enroll Target"
            value={kpis.enroll_target}
            accentBar="green"
            footer="Definition: Partner enrollments • Source: PMO • Updated: 1 day ago"
          />
          <BrandKpiTile
            title="Hit Rate"
            value={`${kpis.hit_rate_percent}%`}
            accentBar={kpis.hit_rate_percent >= 80 ? 'green' : 'bharat'}
            footer="Definition: 90d utilization • Source: OCPP • Updated: 1 hour ago"
          />
          <BrandKpiTile
            title="Enrollment Win Rate"
            value={`${kpis.enrollment_win_rate}%`}
            accentBar="link"
            footer="Definition: Enrollment success rate • Source: OCPI • Updated: 1 day ago"
          />
          <BrandKpiTile
            title="Feedbacks (30d)"
            value={kpis.feedbacks_30d}
            accentBar="primary"
            footer="Definition: Rider feedback count • Source: App • Updated: 1 day ago"
          />
        </div>
      )}

      {/* DSAT Heatmap */}
      <div className="mb-6">
        <GlassCard
          title="Demand Heatmap (Interactive)"
          subtitle="Click cells for details"
          footer="Definition: DemandIndex (weighted Need/Route/Supply/Feasibility) • Source: DSAT Scorer v1 • Updated: 2h ago"
        >
          <DsatHeatmap
            cells={scores}
            onCellClick={handleCellClick}
          />
        </GlassCard>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Top Cells List */}
        <GlassCard
          title="Top Cells by DemandIndex"
          subtitle="Sorted by DemandIndex score"
          footer="Definition: Top ranked cells • Source: DSAT Scorer v1 • Updated: 2h ago"
        >
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {scores.slice(0, 12).map((score) => (
              <div
                key={score.cell}
                onClick={() => handleCellClick(score)}
                className="p-3 border border-gray-200 rounded-2xl hover:shadow-md cursor-pointer transition-all bg-white"
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <MapPin size={16} className="text-brand-purple" />
                    <span className="font-medium text-gray-900">{score.cell}</span>
                    <span className="text-sm text-gray-500">
                      {score.city}
                    </span>
                  </div>
                  <span className="text-sm font-semibold text-brand-purple">
                    DI: {score.demand_index}
                  </span>
                </div>
                <div className="flex items-center gap-3 text-xs">
                  <StatusBadge 
                    status={score.recommend_type === 'dc' ? 'charging' : 'info'}
                    label={score.recommend_type?.toUpperCase() || 'N/A'}
                    size="sm"
                  />
                  <StatusBadge 
                    status={score.recommend_own_or_enroll === 'enroll' ? 'valid' : 'in_progress'}
                    label={score.recommend_own_or_enroll?.toUpperCase() || 'N/A'}
                    size="sm"
                  />
                  <span className="text-gray-400">
                    Enroll Score: {score.enroll_score}
                  </span>
                </div>
                {/* Mini breakdown */}
                <div className="mt-2 grid grid-cols-4 gap-2 text-xs">
                  <div key="need">
                    <div className="text-gray-400">Need</div>
                    <div className="font-medium text-gray-900">{score.need_score}</div>
                  </div>
                  <div key="route">
                    <div className="text-gray-400">Route</div>
                    <div className="font-medium text-gray-900">{score.route_dwell}</div>
                  </div>
                  <div key="gap">
                    <div className="text-gray-400">Gap</div>
                    <div className="font-medium text-gray-900">{score.supply_gap}</div>
                  </div>
                  <div key="feas">
                    <div className="text-gray-400">Feas</div>
                    <div className="font-medium text-gray-900">{score.feasibility}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </GlassCard>

        {/* Cell Breakdown */}
        <GlassCard
          title={cellBreakdown ? `Cell Details: ${cellBreakdown.cell}` : 'Cell Details'}
          subtitle={cellBreakdown ? 'Detailed breakdown' : 'Select a cell to view details'}
          footer="Definition: Detailed breakdown • Source: DSAT Scorer • Updated: Live"
        >
          {cellBreakdown ? (
            <div className="space-y-4">
              {/* Scores */}
              <div>
                <div className="text-sm font-semibold text-gray-900 mb-2">Score Breakdown</div>
                <div className="grid grid-cols-2 gap-3">
                  <div key="need-score" className="p-3 bg-purple-50 rounded-2xl border border-purple-200">
                    <div className="text-xs text-purple-700">Need Score</div>
                    <div className="text-xl font-bold text-purple-900">
                      {cellBreakdown.need_score}
                    </div>
                  </div>
                  <div key="route-dwell" className="p-3 bg-blue-50 rounded-2xl border border-blue-200">
                    <div className="text-xs text-blue-700">Route/Dwell</div>
                    <div className="text-xl font-bold text-blue-900">
                      {cellBreakdown.route_dwell}
                    </div>
                  </div>
                  <div key="supply-gap" className="p-3 bg-orange-50 rounded-2xl border border-orange-200">
                    <div className="text-xs text-orange-700">Supply Gap</div>
                    <div className="text-xl font-bold text-orange-900">
                      {cellBreakdown.supply_gap}
                    </div>
                  </div>
                  <div key="feasibility" className="p-3 bg-green-50 rounded-2xl border border-green-200">
                    <div className="text-xs text-green-700">Feasibility</div>
                    <div className="text-xl font-bold text-green-900">
                      {cellBreakdown.feasibility}
                    </div>
                  </div>
                </div>
              </div>

              {/* Recommendations */}
              <div>
                <div className="text-sm font-semibold text-gray-900 mb-2">Recommendations</div>
                <div className="space-y-2 text-sm">
                  <div className="p-2 bg-blue-50 rounded-xl border border-blue-200">
                    <span className="font-medium text-gray-900">Type:</span> <span className="text-gray-700">{cellBreakdown.rule_cues?.type_decision}</span>
                  </div>
                  <div className="p-2 bg-green-50 rounded-xl border border-green-200">
                    <span className="font-medium text-gray-900">Build/Enroll:</span> <span className="text-gray-700">{cellBreakdown.rule_cues?.enroll_decision}</span>
                  </div>
                </div>
              </div>

              {/* Nearby Chargers */}
              {cellBreakdown.nearby_chargers && cellBreakdown.nearby_chargers.length > 0 && (
                <div>
                  <div className="text-sm font-semibold text-gray-900 mb-2">Nearby Chargers</div>
                  <div className="space-y-2 text-xs">
                    {cellBreakdown.nearby_chargers.slice(0, 3).map((charger, idx) => (
                      <div key={idx} className="p-2 border border-gray-200 rounded-xl bg-white">
                        <div className="font-medium text-gray-900 mb-1">{charger.name}</div>
                        <div className="flex items-center gap-3 text-gray-400">
                          <span>{charger.operator}</span>
                          <span>{charger.type} {charger.power_kw}kW</span>
                          <span>Uptime: {charger.uptime}%</span>
                          <span>{charger.distance_km}km</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-12 text-gray-400">
              <Target size={48} className="mx-auto mb-3 opacity-40" />
              <p>Select a cell from the heatmap to view details</p>
            </div>
          )}
        </GlassCard>
      </div>

      {/* Targets Table */}
      <GlassCard
        title="Monthly Targets"
        subtitle="Approved targets for execution"
        footer="Definition: Approved targets for execution • Source: DSAT Allocator • Updated: 1 day ago"
      >
        <div className="flex items-center gap-2 mb-4">
          <button 
            onClick={handleApproveTop10}
            className="px-4 py-2 text-sm font-medium text-white bg-brand-green hover:opacity-90 rounded-2xl transition-opacity flex items-center gap-2 shadow-sm"
          >
            <CheckCircle size={16} />
            Approve Top 10
          </button>
          <button className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 border border-gray-200 rounded-2xl transition-colors">
            Create Program Tasks
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left font-semibold text-gray-900">Rank</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-900">City</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-900">Cell</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-900">Type</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-900">Build/Enroll</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-900">Connectors</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-900">ETA</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-900">Owner</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-900">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {targets.slice(0, 15).map((target) => (
                <tr key={target.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <span className="font-semibold text-brand-purple">
                      #{target.rank}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-900">{target.city}</td>
                  <td className="px-4 py-3 font-mono text-xs text-gray-700">{target.cell}</td>
                  <td className="px-4 py-3">
                    <StatusBadge 
                      status={target.type === 'dc' ? 'charging' : 'info'}
                      label={target.type?.toUpperCase() || 'N/A'}
                      size="sm"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge 
                      status={target.own_or_enroll === 'enroll' ? 'valid' : 'in_progress'}
                      label={target.own_or_enroll?.toUpperCase() || 'N/A'}
                      size="sm"
                    />
                  </td>
                  <td className="px-4 py-3 text-center text-gray-900">{target.target_connectors}</td>
                  <td className="px-4 py-3 text-gray-900">{target.eta_days}d</td>
                  <td className="px-4 py-3 text-xs text-gray-400">
                    {target.owner}
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge status={target.status} size="sm" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </GlassCard>
      </div>
    </>
  );
};

export default DsatTargets;
